// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI (owner on Quest, 2026-11-05): "pop up windows/modals CAN'T overlap,
 * doesn't matter if they exist now or if we add them in the future. Two
 * modals/popups can be drawn in the same place by themselves, but if there
 * is another one that should be drawn there it should be gently moved to a
 * proper place."
 *
 * Every panel the headset shows is laid out here, once a frame, after the
 * code that places each one has put it where it wants to be:
 *   - a panel is marked when it is made (`markPanel`); `createCanvasPanel`
 *     marks every panel it builds, so a new panel built with the kit takes
 *     part without anyone remembering to add it;
 *   - what counts as overlap is what the eyes see: each panel is a rectangle
 *     of angles round the eye (left-right, up-down), so a near panel in
 *     front of a far one overlaps it as much as two side by side do;
 *   - the panel shown first keeps its place; one shown later that would
 *     cover it stands at the nearest free spot instead, at the same distance
 *     from the eyes, off the globe when that is not much further. Anchors (the
 *     dock, the date track, the Place button) never move and are kept clear,
 *     except by the keyboard while you type (`overAnchors`): 60° wide at
 *     arm's length, it could not stand clear of the dock with Browse and the
 *     legend open and still be in front of you. It covers them no more than
 *     where it opened, so a move never hides the dock's buttons;
 *   - a panel appears at its free spot (it never shows over another, even
 *     for a moment); after that it glides, so a spot that frees up or a
 *     neighbour that moves brings it back or along gently;
 *   - a panel in transit (shrinking toward somewhere, scale under 1) and a
 *     pointer's readout (`role: 'free'`) are left alone.
 *
 * The rules (`freeSpot`) are pure and tested; `layoutPanels` measures the
 * panels, asks them, and moves the meshes.
 */

import type * as THREE from 'three'

/** How a panel takes part: moves aside when it must (`panel`), never moves and is kept clear (`anchor`), or is not laid out (`free`). */
export type PanelRole = 'panel' | 'anchor' | 'free'

export interface PanelLayoutInfo {
  /** The plane's size in metres, before the mesh's scale. */
  width: number
  height: number
  role?: PanelRole
  /**
   * It may stand in front of the anchors (the keyboard, while you type: the
   * dock and the date track are controls, not popups) as much as where it
   * opened, never further onto them, and never over a panel.
   */
  overAnchors?: boolean
}

/** A rectangle of angles round the eye: centre and half sizes, in radians. */
export interface LayoutRect {
  cx: number
  cy: number
  hw: number
  hh: number
}

/** The globe as the eye sees it: centre and angular radius, in radians. */
export interface LayoutCircle {
  cx: number
  cy: number
  r: number
}

export interface LayoutOffset {
  dx: number
  dy: number
}

/** The gap kept between two panels, in radians (about 1.1°). */
export const LAYOUT_GAP_RAD = 0.02
/** How far a panel may be moved, in radians: about 80° sideways, 50° up or down. */
const MAX_DX = 1.4
const MAX_DY = 0.87
/**
 * Where a moved panel may stand, as the eyes see it: from about 43° below
 * the horizon to 34° above, so a panel moved aside can still be read and
 * pressed (the keyboard pushed down to the chest could not be typed on). A
 * panel whose own spot is outside the band may stay as far out as it is.
 */
export const LAYOUT_LOWEST_RAD = -0.75
export const LAYOUT_HIGHEST_RAD = 0.6
/** What a spot over the globe adds to the way there, in radians (about 17°). */
export const GLOBE_COST_RAD = 0.3
/** A spot kept from the frame before wins unless another is this much nearer (no flicker between two). */
const KEEP_MARGIN_RAD = 0.03
/** The glide's time constant, in seconds. */
const GLIDE_TAU_S = 0.18
/** Extra room tried past each edge, in radians (0°, 2°, 4°, 7°). */
const EDGE_PADS = [0, 0.035, 0.07, 0.12]

export function rectsOverlap(a: LayoutRect, b: LayoutRect, gap = LAYOUT_GAP_RAD): boolean {
  return Math.abs(a.cx - b.cx) < a.hw + b.hw + gap && Math.abs(a.cy - b.cy) < a.hh + b.hh + gap
}

export function rectHitsCircle(a: LayoutRect, c: LayoutCircle): boolean {
  const nx = Math.min(Math.max(c.cx, a.cx - a.hw), a.cx + a.hw)
  const ny = Math.min(Math.max(c.cy, a.cy - a.hh), a.cy + a.hh)
  return (nx - c.cx) ** 2 + (ny - c.cy) ** 2 < c.r * c.r
}

function overlapArea(a: LayoutRect, b: LayoutRect): number {
  const w = Math.min(a.cx + a.hw, b.cx + b.hw) - Math.max(a.cx - a.hw, b.cx - b.hw)
  const h = Math.min(a.cy + a.hh, b.cy + b.hh) - Math.max(a.cy - a.hh, b.cy - b.hh)
  return w > 0 && h > 0 ? w * h : 0
}

const moved = (r: LayoutRect, o: LayoutOffset): LayoutRect => ({ ...r, cx: r.cx + o.dx, cy: r.cy + o.dy })

/**
 * Where a panel that wants `want` stands so it covers none of `placed`:
 * its own spot when that is free, else the nearest free one, as an offset
 * from its own spot. A spot over the globe counts as `GLOBE_COST_RAD`
 * further (unless the panel wanted to cover the globe already): panels keep
 * off the data, but the keyboard may rise in front of the globe's lower
 * half rather than swing far to one side. `previous` (last frame's answer) is kept while it is still free and
 * nearly as near as the best. When no spot is free, the one covering least.
 *
 * `rectAt` gives the panel's exact extent at an offset (a panel near the eyes
 * and well below them changes its spread as it moves and turns to face
 * them); without it the rectangle just moves.
 *
 * `tolerated` (the anchors, for the keyboard) may be covered as much as the
 * panel's own spot covers them, never more: moved aside, it never slides
 * further over the dock's buttons.
 */
export function freeSpot(
  want: LayoutRect,
  placed: readonly LayoutRect[],
  globe: LayoutCircle | null = null,
  previous: LayoutOffset | null = null,
  gap = LAYOUT_GAP_RAD,
  rectAt: (o: LayoutOffset) => LayoutRect = (o) => moved(want, o),
  tolerated: readonly LayoutRect[] = [],
): LayoutOffset {
  const coverOf = (r: LayoutRect): number => tolerated.reduce((sum, p) => sum + overlapArea(r, p), 0)
  const ownCover = tolerated.length ? coverOf(rectAt({ dx: 0, dy: 0 })) : 0
  const avoid = globe && !rectHitsCircle(want, globe) ? globe : null
  const lowest = Math.min(LAYOUT_LOWEST_RAD, want.cy)
  const highest = Math.max(LAYOUT_HIGHEST_RAD, want.cy)
  const free = (o: LayoutOffset): boolean => {
    const r = rectAt(o)
    if (r.cy < lowest - 1e-9 || r.cy > highest + 1e-9) return false
    if (tolerated.length && coverOf(r) > ownCover + 1e-6) return false
    return !placed.some((p) => rectsOverlap(r, p, gap))
  }
  const costOf = (o: LayoutOffset): number => Math.hypot(o.dx, o.dy) + (avoid && rectHitsCircle(rectAt(o), avoid) ? GLOBE_COST_RAD : 0)
  const zero = { dx: 0, dy: 0 }
  if (free(zero)) return zero
  // Every spot where the panel just touches one placed panel's side, top or
  // bottom, and their crossings: the nearest free spot is one of them. Each
  // also a little further out, for a panel whose spread grows as it moves.
  const nudge = 1e-4
  const xs = [want.cx]
  const ys = [want.cy]
  for (const pad of EDGE_PADS) {
    for (const p of placed) {
      xs.push(p.cx - (p.hw + want.hw + gap + pad) - nudge, p.cx + p.hw + want.hw + gap + pad + nudge)
      ys.push(p.cy - (p.hh + want.hh + gap + pad) - nudge, p.cy + p.hh + want.hh + gap + pad + nudge)
    }
    if (avoid) {
      xs.push(avoid.cx - avoid.r - want.hw - pad - nudge, avoid.cx + avoid.r + want.hw + pad + nudge)
      ys.push(avoid.cy - avoid.r - want.hh - pad - nudge, avoid.cy + avoid.r + want.hh + pad + nudge)
    }
  }
  let best: LayoutOffset | null = null
  let bestCost = Infinity
  let least: LayoutOffset = zero
  let leastCover = Infinity
  for (const x of xs) {
    for (const y of ys) {
      const o = { dx: x - want.cx, dy: y - want.cy }
      if (Math.abs(o.dx) > MAX_DX || Math.abs(o.dy) > MAX_DY) continue
      if (y < lowest - 1e-9 || y > highest + 1e-9) continue
      const cost = costOf(o)
      if (free(o)) {
        if (cost < bestCost) {
          best = o
          bestCost = cost
        }
      } else if (!best) {
        const r = rectAt(o)
        const cover = placed.reduce((sum, p) => sum + overlapArea(r, p), 0)
        if (cover < leastCover - 1e-9 || (Math.abs(cover - leastCover) <= 1e-9 && cost < Math.hypot(least.dx, least.dy))) {
          least = o
          leastCover = cover
        }
      }
    }
  }
  if (!best) return least
  if (previous && free(previous) && costOf(previous) <= bestCost + KEEP_MARGIN_RAD) return previous
  return best
}

export interface LayoutItem {
  rect: LayoutRect
  anchor: boolean
  /** When it was first shown (earlier keeps its place); ties go by `seq`, the order panels were made. */
  since: number
  seq: number
  previous: LayoutOffset | null
  /** Its exact extent at an offset (see `freeSpot`). */
  rectAt?: (o: LayoutOffset) => LayoutRect
  /** It may stand in front of the anchors (`PanelLayoutInfo.overAnchors`). */
  overAnchors?: boolean
}

/** Every panel's offset: anchors stay, then each in the order shown takes its free spot among those placed before it. */
export function layOut(items: readonly LayoutItem[], globe: LayoutCircle | null = null): LayoutOffset[] {
  const out: LayoutOffset[] = items.map(() => ({ dx: 0, dy: 0 }))
  const anchors: LayoutRect[] = items.filter((item) => item.anchor).map((item) => item.rect)
  const placed: LayoutRect[] = []
  const order = items
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => !item.anchor)
    .sort((a, b) => a.item.since - b.item.since || a.item.seq - b.item.seq)
  for (const { item, index } of order) {
    const offset = item.overAnchors
      ? freeSpot(item.rect, placed, globe, item.previous, LAYOUT_GAP_RAD, item.rectAt, anchors)
      : freeSpot(item.rect, [...anchors, ...placed], globe, item.previous, LAYOUT_GAP_RAD, item.rectAt)
    out[index] = offset
    placed.push(item.rectAt ? item.rectAt(offset) : moved(item.rect, offset))
  }
  return out
}

// --- The headset's panels ---

interface Entry {
  info: PanelLayoutInfo
  seq: number
  since: number | null
  /** Where the placing code put it (the parent's frame), and where the layout then put it. */
  wanted: THREE.Vector3 | null
  applied: THREE.Vector3 | null
  /** The glide's current offset, and the spot it glides to. */
  offset: LayoutOffset
  target: LayoutOffset | null
}

const entries = new Map<THREE.Object3D, Entry>()
let seqCounter = 0
let frameCounter = 0

/** Lay this panel out with the others from now on (`createCanvasPanel` does it for every panel it makes). */
export function markPanel(obj: THREE.Object3D, info: PanelLayoutInfo): void {
  const old = entries.get(obj)
  entries.set(obj, { info, seq: old?.seq ?? seqCounter++, since: null, wanted: null, applied: null, offset: { dx: 0, dy: 0 }, target: null })
}

/** Stop laying it out (its panel was disposed). */
export function unmarkPanel(obj: THREE.Object3D): void {
  entries.delete(obj)
}

/** For tests and the browser checks: what a mesh was marked with. */
export function panelLayoutInfo(obj: THREE.Object3D): PanelLayoutInfo | null {
  return entries.get(obj)?.info ?? null
}

function shownInScene(obj: THREE.Object3D): boolean {
  let o: THREE.Object3D | null = obj
  while (o) {
    if (!o.visible) return false
    if ((o as THREE.Scene).isScene) return true
    o = o.parent
  }
  return false
}

/**
 * Once a frame, after every panel has been placed: move the panels that
 * would cover another to their free spots. `eye` is the viewer's eyes,
 * `gazeYaw` the way they face (radians, 0 toward -Z), `globe` the globe's
 * centre and radius while the viewer is outside it.
 */
export function layoutPanels(
  THREE_: typeof THREE,
  eye: THREE.Vector3,
  dtSeconds: number,
  globe: { centre: THREE.Vector3; radius: number } | null,
  gazeYaw = 0,
): void {
  frameCounter++
  const world = new THREE_.Vector3()
  const scale = new THREE_.Vector3()
  const corner = new THREE_.Vector3()
  const dir = new THREE_.Vector3()
  const xAxis = new THREE_.Vector3()
  const yAxis = new THREE_.Vector3()
  const zAxis = new THREE_.Vector3()
  const worldUp = new THREE_.Vector3(0, 1, 0)
  const shown: Array<{ obj: THREE.Object3D; entry: Entry; dist: number; az: number; el: number }> = []
  const items: LayoutItem[] = []
  const wrap = (a: number): number => Math.atan2(Math.sin(a), Math.cos(a))
  for (const [obj, entry] of entries) {
    const role = entry.info.role ?? 'panel'
    if (role === 'free' || !shownInScene(obj)) {
      entry.since = null
      entry.target = null
      entry.offset = { dx: 0, dy: 0 }
      continue
    }
    // Where the placing code wants it this frame: a panel placed once (the
    // keyboard) still stands where the layout last put it, so its own spot is the one kept.
    if (entry.applied && obj.position.equals(entry.applied) && entry.wanted) obj.position.copy(entry.wanted)
    entry.wanted = (entry.wanted ?? new THREE_.Vector3()).copy(obj.position)
    obj.getWorldScale(scale)
    if (role === 'panel' && Math.min(scale.x, scale.y) < 0.98) {
      // In transit (the legend shrinking on its way): left alone this frame.
      entry.applied = null
      entry.offset = { dx: 0, dy: 0 }
      continue
    }
    obj.updateMatrixWorld(true)
    obj.getWorldPosition(world)
    const d = world.clone().sub(eye)
    const dist = d.length()
    if (dist < 0.05) continue
    const az = wrap(Math.atan2(d.x, -d.z) - gazeYaw)
    const el = Math.atan2(d.y, Math.hypot(d.x, d.z))
    // Its extent as the eyes see it, from its four corners: a panel near the
    // eyes and well below them (the keyboard) spreads wider at its lower edge.
    let hw = 0
    let hh = 0
    const w = (entry.info.width * scale.x) / 2
    const h = (entry.info.height * scale.y) / 2
    for (const [cx, cy] of [[-w, -h], [w, -h], [w, h], [-w, h]] as const) {
      corner.set(cx / scale.x, cy / scale.y, 0).applyMatrix4(obj.matrixWorld).sub(eye)
      hw = Math.max(hw, Math.abs(wrap(Math.atan2(corner.x, -corner.z) - gazeYaw - az)))
      hh = Math.max(hh, Math.abs(Math.atan2(corner.y, Math.hypot(corner.x, corner.z)) - el))
    }
    // Where it may go it faces the eyes (the layout turns it so): its extent there, worked out.
    const rectAt = role === 'anchor' ? undefined : (o: LayoutOffset): LayoutRect => {
      const a = az + o.dx + gazeYaw
      const e = el + o.dy
      dir.set(Math.cos(e) * Math.sin(a), Math.sin(e), -Math.cos(e) * Math.cos(a))
      // three's lookAt: +Z toward the eyes, X = up × Z, Y = Z × X.
      zAxis.copy(dir).negate()
      xAxis.crossVectors(worldUp, zAxis).normalize()
      yAxis.crossVectors(zAxis, xAxis)
      let rw = 0
      let rh = 0
      for (const [cx, cy] of [[-w, -h], [w, -h], [w, h], [-w, h]] as const) {
        corner.copy(dir).multiplyScalar(dist).addScaledVector(xAxis, cx).addScaledVector(yAxis, cy)
        rw = Math.max(rw, Math.abs(wrap(Math.atan2(corner.x, -corner.z) - a)))
        rh = Math.max(rh, Math.abs(Math.atan2(corner.y, Math.hypot(corner.x, corner.z)) - e))
      }
      return { cx: az + o.dx, cy: el + o.dy, hw: rw, hh: rh }
    }
    if (entry.since === null) entry.since = frameCounter
    shown.push({ obj, entry, dist, az, el })
    items.push({ rect: rectAt ? rectAt({ dx: 0, dy: 0 }) : { cx: az, cy: el, hw, hh }, anchor: role === 'anchor', since: entry.since, seq: entry.seq, previous: entry.target, rectAt, overAnchors: entry.info.overAnchors === true })
  }
  let circle: LayoutCircle | null = null
  if (globe) {
    const d = globe.centre.clone().sub(eye)
    const dist = d.length()
    if (dist > globe.radius * 1.02) {
      circle = { cx: wrap(Math.atan2(d.x, -d.z) - gazeYaw), cy: Math.atan2(d.y, Math.hypot(d.x, d.z)), r: Math.asin(Math.min(0.999, globe.radius / dist)) }
    }
  }
  const offsets = layOut(items, circle)
  const k = 1 - Math.exp(-Math.max(0, dtSeconds) / GLIDE_TAU_S)
  shown.forEach(({ obj, entry, dist, az, el }, i) => {
    const target = offsets[i]!
    const first = entry.target === null
    entry.target = target
    // A panel just shown appears at its spot; after that it glides.
    entry.offset = first ? { ...target } : { dx: entry.offset.dx + (target.dx - entry.offset.dx) * k, dy: entry.offset.dy + (target.dy - entry.offset.dy) * k }
    if (Math.abs(entry.offset.dx) < 1e-5 && Math.abs(entry.offset.dy) < 1e-5) {
      entry.applied = obj.position.clone()
      return
    }
    const a = az + entry.offset.dx + gazeYaw
    const e = el + entry.offset.dy
    world.set(Math.cos(e) * Math.sin(a), Math.sin(e), -Math.cos(e) * Math.cos(a)).multiplyScalar(dist).add(eye)
    if (obj.parent) obj.parent.worldToLocal(world)
    obj.position.copy(world)
    obj.lookAt(eye)
    entry.applied = obj.position.clone()
  })
}

/** For tests: forget every panel. */
export function resetPanelLayoutForTests(): void {
  entries.clear()
  seqCounter = 0
  frameCounter = 0
}
